import {
    Battery50Rounded,
    CalendarMonth,
    EvStation,
    LocationOn,
    Receipt,
} from '@mui/icons-material';
import { Box, Button, Grid } from '@mui/material';
import {
    ChargingStation,
    TransactionsDetailData,
} from 'services/centralServerService/data-contracts';
import React, { useEffect } from 'react';

import { Loading } from 'components/Loading';
import { LocalizationKeys } from 'assets/localizations/localizationKeys';
import colors from 'assets/theme/colors';
import moment from 'moment';
import { useStores } from 'stores/storeInitializer';

function showNotification(title: string, body: string, url: string) {
    if (localStorage.getItem(url)) {
        return;
    }

    // Check if the Notification permission has already been granted
    if (Notification.permission === 'granted') {
        // If it's granted, create a notification
        const notification = new Notification(title, {
            body,
            icon: '/logo.svg', // optional
        });

        // Handle the click event on the notification
        notification.onclick = () => {
            window.open(url, '_blank');
        };
    } else if (Notification.permission !== 'denied') {
        // If it's not denied (i.e., "default"), ask for permission
        Notification.requestPermission().then((permission) => {
            // If the permission is granted, send a notification
            if (permission === 'granted') {
                const notification = new Notification(title, {
                    body,
                    icon: '/logo.svg', // optional
                });

                // Handle the click event on the notification
                notification.onclick = () => {
                    window.open(url, '_blank');
                };
            }
        });
    }

    localStorage.setItem(url, 'notified');
}

export const SessionDataList: React.FC<{
    session: TransactionsDetailData | undefined | null;
    charger: ChargingStation | undefined | null;
}> = ({ session, charger }) => {
    const { store } = useStores();

    useEffect(() => {
        const handleUserInteraction = () => {
            if (Notification.permission === 'default') {
                Notification.requestPermission().then((permission) => {
                    console.log('Permission requested:', permission)
                });
            }

            if (session?.billingData?.stop?.invoiceUrl) {
                if (!localStorage.getItem(session.billingData.stop.invoiceUrl)) {
                    console.log('Showing notification')
                    showNotification(
                        store.L(LocalizationKeys.InvoiceReadyTitle),
                        store.L(LocalizationKeys.InvoiceReadyBody),
                        session.billingData.stop.invoiceUrl,
                    );
                    localStorage.setItem(session.billingData.stop.invoiceUrl, 'notified');
                }

            }

            // Remove event listeners after the first interaction to prevent multiple notifications
            window.removeEventListener('scroll', handleUserInteraction);
            window.removeEventListener('touchstart', handleUserInteraction);
            window.removeEventListener('click', handleUserInteraction);
        };

        // Add event listeners for various user interactions
        window.addEventListener('scroll', handleUserInteraction);
        window.addEventListener('touchstart', handleUserInteraction);
        window.addEventListener('click', handleUserInteraction);

        return () => {
            // Cleanup event listeners on component unmount
            window.removeEventListener('scroll', handleUserInteraction);
            window.removeEventListener('touchstart', handleUserInteraction);
            window.removeEventListener('click', handleUserInteraction);
        };
    }, [session, store]);

    return (
        <Grid p={2}>
            <Loading activity={store.activity} />
            <Grid container item xs={12} display={'flex'} alignItems={'center'}>
                <Grid item xs={2} display={'flex'} alignItems={'center'}>
                    <EvStation
                        style={{
                            backgroundColor: '#4CAF50',
                            padding: 5,
                            borderRadius: '100%',
                            color: 'white',
                        }}
                    />
                </Grid>
                <Grid item xs={10} display={'flex'} alignItems={'center'}>
                    {`${session?.chargeBoxID}`}
                </Grid>
            </Grid>
            <Grid
                container
                item
                xs={12}
                display={'flex'}
                alignItems={'center'}
                pt={2}
            >
                <Grid item xs={2}>
                    <LocationOn
                        style={{
                            backgroundColor: '#3375e3',
                            padding: 5,
                            borderRadius: '100%',
                            color: 'white',
                        }}
                    />
                </Grid>
                <Grid item xs={10}>
                    <Box>
                        {Object.values({
                            ...((session as any)?.siteArea?.address || {}),
                            coordinates: '',
                        })
                            ?.filter((el: any) => el && el?.length > 0)
                            ?.join(', ')}
                    </Box>
                </Grid>
            </Grid>
            <Grid
                container
                item
                xs={12}
                display={'flex'}
                alignItems={'center'}
                pt={2}
            >
                <Grid item xs={2}>
                    <CalendarMonth
                        style={{
                            backgroundColor: '#fca949',
                            padding: 5,
                            borderRadius: '100%',
                            color: 'white',
                        }}
                    />
                </Grid>
                <Grid item xs={10}>
                    <Box>
                        {moment(session?.timestamp).format(
                            'DD/MM/YYYY, HH:mm:ss',
                        )}
                    </Box>
                </Grid>
            </Grid>
            {session?.stateOfCharge && session?.stop?.stateOfCharge ? (
                <Grid
                    container
                    item
                    xs={12}
                    display={'flex'}
                    alignItems={'center'}
                    pt={2}
                >
                    <Grid item xs={2}>
                        <Battery50Rounded
                            style={{
                                backgroundColor: '#e24b52',
                                padding: 5,
                                borderRadius: '100%',
                                color: 'white',
                            }}
                        />
                    </Grid>
                    <Grid item xs={10}>
                        <Box>
                            {session?.stateOfCharge} - {' '}
                            {session?.stop?.stateOfCharge}
                        </Box>
                    </Grid>
                </Grid>
            ) : null}
            <Grid
                container
                item
                xs={12}
                display={'flex'}
                alignItems={'center'}
                pt={2}
            >
                <Grid item xs={2}>
                    <Receipt
                        style={{
                            backgroundColor: colors.error.main,
                            padding: 5,
                            borderRadius: '100%',
                            color: 'white',
                        }}
                    />
                </Grid>
                <Grid item xs={10}>
                    {session?.billingData?.stop?.invoiceUrl ? (
                        <Button
                            onClick={() => {
                                (window as any).location.href =
                                    session?.billingData?.stop?.invoiceUrl;
                            }}
                        >
                            {store.L(LocalizationKeys.Invoice)}
                        </Button>
                    ) : (
                        store.L(LocalizationKeys.PendingInvoice)
                    )}
                </Grid>
            </Grid>
        </Grid>
    );
};
